import * as THREE from 'three';
import { SectionConfig, SectionRect, SectionInterface, SectionInsets } from '../types';
import { createSash } from '../geometry/SashGeometry';
import { createGlassPane } from '../geometry/GlassGeometry';
import { createHandle } from '../geometry/HandleGeometry';
import { createRoedenPattern } from '../geometry/RoedenGeometry';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';

/**
 * PIVOT-V: Vertical pivot (taatsraam).
 * Y-axis rotation at an offset pivot point (default center).
 */
export class PivotSection implements SectionInterface {
  readonly group: THREE.Group;
  private pivotGroup: THREE.Group;

  constructor(config: SectionConfig, rect: SectionRect, materialLib: MaterialLibrary, frameMaterial: THREE.Material, insets: SectionInsets) {
    this.group = new THREE.Group();
    this.group.name = `section-pivot-${rect.index}`;
    this.group.userData = { sectionIndex: rect.index, sectionType: 'PIVOT-V' };

    const effW = rect.width - insets.left - insets.right;
    const effH = rect.height - insets.top - insets.bottom;
    const clearW = effW - 2 * PROFILE.sashWidth;
    const clearH = effH - 2 * PROFILE.sashWidth;
    const pivotOffset = config.orientation.pivotOffset ?? 0.5;

    const sashAssembly = new THREE.Group();

    const sash = createSash(effW, effH, PROFILE.sashWidth, PROFILE.sashDepth, frameMaterial);
    sashAssembly.add(sash);

    const glass = createGlassPane(
      clearW, clearH, PROFILE.glassThickness,
      materialLib.glass(config.infill.tint, config.infill.glazingTier),
    );
    glass.position.set(effW / 2, effH / 2, 0);
    sashAssembly.add(glass);

    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(effW / 2, effH / 2, 0);
      sashAssembly.add(roeden);
    }

    const handle = createHandle(materialLib.handleMaterial(config.hardware.handleFinish));
    handle.position.set(effW - PROFILE.sashWidth / 2, effH / 2, PROFILE.sashDepth / 2);
    sashAssembly.add(handle);

    // Pivot at offset position within effective sash width
    const pivotX = insets.left + effW * pivotOffset;
    this.pivotGroup = new THREE.Group();
    this.pivotGroup.position.set(pivotX, 0, 0);
    sashAssembly.position.set(insets.left - pivotX, insets.bottom, 0);
    this.pivotGroup.add(sashAssembly);
    this.group.add(this.pivotGroup);

    if (config.openAmount > 0) {
      this.setOpenAngle(config.openAmount * (Math.PI / 2));
    }

    this.group.position.set(rect.x, rect.y, 0);
  }

  setOpenAngle(rad: number): void {
    this.pivotGroup.rotation.y = rad;
  }
}
